import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { analyzePlacementsSP, analyzePlacementsSPFromAPI, checkMissingDaysPlacements, getCachedAnalysis } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';

const STORAGE_KEY = 'placements_sp_analysis';
var spCache = null;

/* ───────────────────────────────────────── */
/* Placements SP — full port of placements.py */
/* ───────────────────────────────────────── */

var COLORS = ['#818CF8', '#F87171', '#34D399', '#FBBF24', '#60A5FA', '#A78BFA', '#FB923C'];

var DARK_LAYOUT = {
    paper_bgcolor: '#111827',
    plot_bgcolor: '#111827',
    font: { color: '#E5E7EB' },
    xaxis: { gridcolor: '#374151', zerolinecolor: '#374151' },
    yaxis: { gridcolor: '#374151', zerolinecolor: '#374151' },
    legend: { bgcolor: 'rgba(0,0,0,0)', font: { color: '#E5E7EB' } },
};

function PlacementsSP() {
    var _a = useState(null), file = _a[0], setFile = _a[1];
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var _c = useState(''), error = _c[0], setError = _c[1];
    var _d = useState(null), result = _d[0], setResult = _d[1];
    var _e = useState(''), portfolio = _e[0], setPortfolio = _e[1];

    var _ds = useState('file'), dataSource = _ds[0], setDataSource = _ds[1];
    var _sd = useState(function () { var d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); }), startDate = _sd[0], setStartDate = _sd[1];
    var _ed = useState(function () { return new Date().toISOString().slice(0, 10); }), endDate = _ed[0], setEndDate = _ed[1];
    var _sm = useState(''), statusMsg = _sm[0], setStatusMsg = _sm[1];

    var fileRef = useRef(file);
    var hasResult = useRef(false);
    useEffect(function () { fileRef.current = file; }, [file]);

    useEffect(function () {
        var pending = localStorage.getItem('placements-sp-pending');
        console.log('PENDING:', pending);

        if (pending === 'true') {
            var currentPid = localStorage.getItem('selectedAccountValue');
            console.log('REQUESTING CACHE FOR:', currentPid);

            if (!currentPid) return;
            setLoading(true);
            axios.get(
                `${process.env.REACT_APP_API_URL}/analysis/placements-sp/latest/${currentPid}`
            )
            .then(function (res) {
                console.log('CACHE RESPONSE:', res.data);
                if (res.data && res.data.data) {
                    console.log('Placements result restored from cache');
                    setResult({
                        ...res.data.data
                    });
                    setLoading(false);
                    hasResult.current = true;
                    localStorage.removeItem('placements-sp-pending');
                }
            })
            .catch(function (err) {
                setLoading(false);
                console.log('CACHE ERROR:', err);
                console.log('Placements result not ready yet');
            });
        } else {
            async function load() {
                try {
                    var saved = spCache || JSON.parse(sessionStorage.getItem(STORAGE_KEY));
                    if (saved && saved.result) {
                        setResult(saved.result);
                        hasResult.current = true;
                        if (saved.portfolio) setPortfolio(saved.portfolio);
                        if (saved.dataSource) setDataSource(saved.dataSource);
                        if (saved.startDate) setStartDate(saved.startDate);
                        if (saved.endDate) setEndDate(saved.endDate);
                        return;
                    }
                    var pid = localStorage.getItem('selectedAccountValue');
                    if (pid) {
                        var backend = await getCachedAnalysis(pid, 'sp');
                        if (backend.success && backend.data) {
                            setResult(backend.data);
                            hasResult.current = true;
                        }
                    }
                } catch (e) {}
            }
            load();
        }
    }, []);

    var doAnalyze = useCallback(async function (portfolioOverride) {
        if (dataSource === 'file') {
            var f = fileRef.current;
            if (!f) return;
        }
        if (dataSource === 'api') {
            var pid = localStorage.getItem('selectedAccountValue');
            if (!pid) {
                setError('Please select an Amazon Ads account in the header before using API mode.');
                return;
            }
        }
        
        if (dataSource === 'api') {
            localStorage.setItem('placements-sp-pending', 'true');
        }

        const executeAnalysis = async () => {
            setLoading(true);
            setError('');
            setStatusMsg(dataSource === 'api' ? '🔄 Fetching from Amazon API — this may take a few minutes...' : '🔄 Processing file...');
            try {
                var portVal = portfolioOverride !== undefined ? portfolioOverride : (portfolio || undefined);
                var res;
                if (dataSource === 'api') {
                    var currentPid = localStorage.getItem('selectedAccountValue');
                    res = await analyzePlacementsSPFromAPI(currentPid, startDate, endDate, portVal);
                } else {
                    res = await analyzePlacementsSP(fileRef.current, portVal);
                }
                setResult(res.data);
                hasResult.current = true;
                try {
                    var saveData = {
                        result: res.data,
                        portfolio: portfolio,
                        dataSource: dataSource,
                        startDate: startDate,
                        endDate: endDate,
                    };
                    spCache = saveData;
                    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saveData));
                } catch (e) {
                    if (!spCache) spCache = { result: res.data };
                }

                // Notificación Modal Sutil
                const hasData = res.data && res.data.aggregated && res.data.aggregated.length > 0;
                if (hasData) {
                    alertify.success('Data loaded and charts generated successfully!');
                } else {
                    alertify.confirm(
                        'Insufficient Data',
                        'Not enough data found for the selected date range and portfolio. Would you like to request a broader date range from the Amazon API?',
                        function () {
                            setDataSource('api');
                            alertify.message('Please select a new date range and analyze again.');
                        },
                        function () {
                            alertify.error('Analysis canceled.');
                        }
                    ).set('labels', {ok: 'Yes, fetch from API', cancel: 'Cancel'});
                }

            } catch (err) {
                setError(err.response && err.response.data ? err.response.data.message : err.message);
                setStatusMsg('');
            } finally {
                setLoading(false);
            }
        };

        if (dataSource === 'api') {
            var currentPid = localStorage.getItem('selectedAccountValue');
            try {
                setLoading(true);
                setStatusMsg('⏳ Checking local cache...');
                const checkRes = await checkMissingDaysPlacements(currentPid, startDate, endDate, 'sp');
                if (checkRes.missingDays > 0) {
                    setLoading(false);
                    setStatusMsg(`📡 ${checkRes.missingDays} days not found in database — Amazon API needed`);
                    alertify.confirm(
                        'Amazon API Fetch Required',
                        `We need to fetch ${checkRes.missingDays} missing days from Amazon API. This may take a few minutes. Continue?`,
                        function () { executeAnalysis(); },
                        function () { alertify.error('Analysis canceled.'); localStorage.removeItem('placements-sp-pending'); setStatusMsg(''); }
                    ).set('labels', {ok: 'Yes, fetch from API', cancel: 'Cancel'});
                    return;
                } else {
                    setStatusMsg('✅ All data found in database — loading locally...');
                }
            } catch (err) {
                console.error("Error pre-flight check Placements", err);
                setStatusMsg('');
            }
        }

        executeAnalysis();
    }, [portfolio, dataSource, startDate, endDate, setError, setLoading, setResult]);

    var handleSubmit = function (e) {
        e.preventDefault();
        doAnalyze();
    };

    var handlePortfolioChange = function (val) {
        setPortfolio(val);
        if (hasResult.current && fileRef.current) {
            setTimeout(function () { doAnalyze(val); }, 50);
        }
    };

    /* Helper: stacked bar traces by date */
    var buildStackedTraces = function (data, yKey) {
        if (!data || data.length === 0) return [];
        var placements = [];
        var seen = {};
        data.forEach(function (r) { if (!seen[r.placement]) { seen[r.placement] = true; placements.push(r.placement); } });
        return placements.map(function (p, i) {
            var subset = data.filter(function (r) { return r.placement === p; });
            return {
                x: subset.map(function (r) { return r.date; }),
                y: subset.map(function (r) { return r[yKey]; }),
                type: 'bar', name: p,
                marker: { color: COLORS[i % COLORS.length] },
            };
        });
    };

    /* Helper: line traces by date */
    var buildLineTraces = function (data, yKey) {
        if (!data || data.length === 0) return [];
        var placements = [];
        var seen = {};
        data.forEach(function (r) { if (!seen[r.placement]) { seen[r.placement] = true; placements.push(r.placement); } });
        return placements.map(function (p, i) {
            var subset = data.filter(function (r) { return r.placement === p; });
            return {
                x: subset.map(function (r) { return r.date; }),
                y: subset.map(function (r) { return r[yKey]; }),
                type: 'scatter', mode: 'lines+markers', name: p,
                line: { color: COLORS[i % COLORS.length], width: 2 },
                marker: { size: 4 },
            };
        });
    };

    var buildPieChart = function (key, title) {
        if (!result || !result.distributions) return null;
        return {
            values: result.distributions.map(function (d) { return d[key]; }),
            labels: result.distributions.map(function (d) { return d.placement; }),
            type: 'pie',
            name: title,
            hovertemplate: '%{label}: %{value:.1f}%<extra></extra>',
        };
    };

    var charts = result && result.placementCharts ? result.placementCharts : null;

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Placements Analysis — Sponsored Products</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Upload / API */}
                <div className="card mb-4">
                    <div className="card-body">
                        {/* Mode Toggle */}
                        <div className="btn-group mb-3" role="group">
                            <button type="button" className={'btn ' + (dataSource === 'file' ? 'btn-primary' : 'btn-outline-primary')}
                                onClick={function () { setDataSource('file'); }}>
                                <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload File
                            </button>
                            <button type="button" className={'btn ' + (dataSource === 'api' ? 'btn-primary' : 'btn-outline-primary')}
                                onClick={function () { setDataSource('api'); }}>
                                <i className="bi bi-cloud-download me-1"></i> Fetch from Amazon API
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <div className="col-md-4">
                                        <label className="form-label">Campaign Report (Excel)</label>
                                        <input type="file" className="form-control" accept=".xlsx,.xls,.csv"
                                            onChange={function (e) { setFile(e.target.files[0]); }} required />
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-md-3">
                                            <label className="form-label">Start Date</label>
                                            <input type="date" className="form-control" value={startDate}
                                                onChange={function (e) { setStartDate(e.target.value); }} required />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">End Date</label>
                                            <input type="date" className="form-control" value={endDate}
                                                onChange={function (e) { setEndDate(e.target.value); }} required />
                                        </div>
                                    </>
                                )}
                                <div className="col-md-4">
                                    <label className="form-label">Portfolio (optional)</label>
                                    {result && result.portfolios ? (
                                        <select className="form-select" value={portfolio}
                                            onChange={function (e) { handlePortfolioChange(e.target.value); }}>
                                            <option value="">All portfolios</option>
                                            {result.portfolios.map(function (p) { return <option key={p} value={p}>{p}</option>; })}
                                        </select>
                                    ) : (
                                        <input className="form-control" value={portfolio}
                                            onChange={function (e) { setPortfolio(e.target.value); }} placeholder="Enter portfolio name..." />
                                    )}
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary w-100"
                                        disabled={loading || (dataSource === 'file' && !file)}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                            {dataSource === 'api' && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Account: <strong>{localStorage.getItem('selectedAccountName') || 'Not selected'}</strong>
                                    </small>
                                </div>
                            )}
                        </form>
                        {/* Status indicator */}
                        {(loading || statusMsg) && (
                            <div className="mt-2 d-flex align-items-center" style={{ minHeight: 24 }}>
                                {loading && <span className="spinner-border spinner-border-sm text-primary me-2" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                                {statusMsg && <small className="text-muted" style={{ fontSize: '0.8rem' }}>{statusMsg}</small>}
                            </div>
                        )}
                    </div>
                </div>

                {/* ═══════════ TRACKING2 CHARTS — shown FIRST ═══════════ */}
                {charts && (
                    <>
                        {/* 1) Click Distribution by Placement (bar) */}
                        {charts.clicksByPlacement && charts.clicksByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={[{
                                            x: charts.clicksByPlacement.map(function (r) { return r.placement; }),
                                            y: charts.clicksByPlacement.map(function (r) { return r.clicks; }),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Clicks: %{y:,}<extra></extra>',
                                        }]}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Click Distribution by Placement', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Placement', tickangle: -45 }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Total Clicks' }),
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 2) Orders Distribution by Placement (bar) */}
                        {charts.ordersByPlacement && charts.ordersByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={[{
                                            x: charts.ordersByPlacement.map(function (r) { return r.placement; }),
                                            y: charts.ordersByPlacement.map(function (r) { return r.orders; }),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Orders: %{y:,}<extra></extra>',
                                        }]}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Orders Distribution by Placement', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Placement', tickangle: -45 }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Total Orders (7 Days)' }),
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 3) Clicks vs Orders % Distribution (grouped bar) */}
                        {charts.clicksVsOrdersPct && charts.clicksVsOrdersPct.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={[
                                            {
                                                x: charts.clicksVsOrdersPct.map(function (r) { return r.placement; }),
                                                y: charts.clicksVsOrdersPct.map(function (r) { return r.clicksPct; }),
                                                type: 'bar', name: 'Clicks Percentage',
                                                marker: { color: COLORS[0] },
                                            },
                                            {
                                                x: charts.clicksVsOrdersPct.map(function (r) { return r.placement; }),
                                                y: charts.clicksVsOrdersPct.map(function (r) { return r.ordersPct; }),
                                                type: 'bar', name: 'Orders Percentage',
                                                marker: { color: COLORS[1] },
                                            },
                                        ]}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Clicks vs Orders Percentage Distribution by Placement', font: { size: 16, color: '#E5E7EB' } },
                                            barmode: 'group',
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Placement', tickangle: -45 }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Percentage (%)' }),
                                            legend: Object.assign({}, DARK_LAYOUT.legend, { title: { text: 'Metric' } }),
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 4) Orders Evolution by Placement Over Time (line) */}
                        {charts.ordersByDate && charts.ordersByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={buildLineTraces(charts.ordersByDate, 'orders')}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Orders Evolution by Placement Over Time', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Date', type: 'date' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Total Orders (7 Days)' }),
                                            legend: Object.assign({}, DARK_LAYOUT.legend, { title: { text: 'Placement' } }),
                                            margin: { l: 60, r: 20, t: 50, b: 60 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 5) Spend % by Placement (stacked bar) */}
                        {charts.spendPctByDate && charts.spendPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={buildStackedTraces(charts.spendPctByDate, 'spendPct')}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Evolution of Spend Percentage by Placement Over Time', font: { size: 16, color: '#E5E7EB' } },
                                            barmode: 'stack',
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Date', type: 'date' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Spend Percentage (%)' }),
                                            legend: Object.assign({}, DARK_LAYOUT.legend, { title: { text: 'Placement' } }),
                                            margin: { l: 60, r: 20, t: 50, b: 60 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 6) Clicks % by Placement (stacked bar) */}
                        {charts.clicksPctByDate && charts.clicksPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={buildStackedTraces(charts.clicksPctByDate, 'clicksPct')}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Evolution of Clicks Percentage by Placement Over Time', font: { size: 16, color: '#E5E7EB' } },
                                            barmode: 'stack',
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Date', type: 'date' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Clicks Percentage (%)' }),
                                            legend: Object.assign({}, DARK_LAYOUT.legend, { title: { text: 'Placement' } }),
                                            margin: { l: 60, r: 20, t: 50, b: 60 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 7) Top Campaigns by Clicks in Product Pages (horizontal bar + Viridis) */}
                        {charts.topCampaignsByClicks && charts.topCampaignsByClicks.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsByClicks.map(function (r) { return r.campaign; }).reverse(),
                                            x: charts.topCampaignsByClicks.map(function (r) { return r.clicks; }).reverse(),
                                            type: 'bar', orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsByClicks.map(function (r) { return r.clicks; }).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Clicks: %{x:,}<extra></extra>',
                                        }]}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Campaigns with Highest Clicks in Product Pages', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Total Clicks' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Campaign Name', automargin: true }),
                                            margin: { l: 280, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsByClicks.length * 28 + 100),
                                            showlegend: false,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 8) Top Campaigns by Spend in Product Pages (table + chart) */}
                        {charts.topCampaignsBySpend && charts.topCampaignsBySpend.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5 className="mb-3">Top Spend in Product Pages (&gt; $10)</h5>
                                    <div className="table-responsive mb-3">
                                        <table className="table table-sm table-striped">
                                            <thead className="table-dark">
                                                <tr><th>Campaign Name</th><th>Total Spend</th></tr>
                                            </thead>
                                            <tbody>
                                                {charts.topCampaignsBySpend.map(function (r, i) {
                                                    return (
                                                        <tr key={i}>
                                                            <td>{r.campaign}</td>
                                                            <td>${r.spend.toFixed(2)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsBySpend.map(function (r) { return r.campaign; }).reverse(),
                                            x: charts.topCampaignsBySpend.map(function (r) { return r.spend; }).reverse(),
                                            type: 'bar', orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsBySpend.map(function (r) { return r.spend; }).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Spend: $%{x:,.2f}<extra></extra>',
                                        }]}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Campaigns with Highest Spend in Product Pages (> $10)', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Total Spend' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Campaign Name', automargin: true }),
                                            margin: { l: 280, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsBySpend.length * 28 + 100),
                                            showlegend: false,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 9) Spend Evolution by Placement Over Time (line) */}
                        {charts.spendByDate && charts.spendByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <Plot
                                        data={buildLineTraces(charts.spendByDate, 'spend')}
                                        layout={Object.assign({}, DARK_LAYOUT, {
                                            title: { text: 'Spend Evolution by Placement Over Time', font: { size: 16, color: '#E5E7EB' } },
                                            xaxis: Object.assign({}, DARK_LAYOUT.xaxis, { title: 'Date', type: 'date' }),
                                            yaxis: Object.assign({}, DARK_LAYOUT.yaxis, { title: 'Total Spend' }),
                                            legend: Object.assign({}, DARK_LAYOUT.legend, { title: { text: 'Placement' } }),
                                            margin: { l: 60, r: 20, t: 50, b: 60 },
                                            height: 420,
                                        })}
                                        config={{ responsive: true, displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ═══════════ ORIGINAL CONTENT — KPI cards, pies, drill-down (below) ═══════════ */}

                {/* KPI cards */}
                {result && result.aggregated && (
                    <div className="row mb-4 g-3">
                        {result.aggregated.map(function (p) {
                            return (
                                <div className="col-md-4" key={p.placement}>
                                    <div className="card">
                                        <div className="card-body">
                                            <h6 className="card-subtitle text-muted mb-2">{p.placement}</h6>
                                            <div className="row text-center">
                                                <div className="col-4"><small>CTR</small><br /><strong>{p.ctr}%</strong></div>
                                                <div className="col-4"><small>CVR</small><br /><strong>{p.cvr}%</strong></div>
                                                <div className="col-4"><small>ACOS</small><br /><strong>{p.acos}%</strong></div>
                                            </div>
                                            <hr />
                                            <div className="row text-center">
                                                <div className="col-3"><small>Clicks</small><br />{p.clicks.toLocaleString()}</div>
                                                <div className="col-3"><small>Orders</small><br />{p.orders.toLocaleString()}</div>
                                                <div className="col-3"><small>Spend</small><br />${p.spend.toFixed(0)}</div>
                                                <div className="col-3"><small>Sales</small><br />${p.sales.toFixed(0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Distribution pie charts */}
                {result && result.distributions && (
                    <div className="row mb-4">
                        {[
                            { key: 'clicksPct', title: 'Clicks Distribution' },
                            { key: 'spendPct', title: 'Spend Distribution' },
                            { key: 'salesPct', title: 'Sales Distribution' },
                            { key: 'ordersPct', title: 'Orders Distribution' },
                        ].map(function (item) {
                            return (
                                <div className="col-md-3" key={item.key}>
                                    <div className="card">
                                        <div className="card-body p-2">
                                            <Plot
                                                data={[buildPieChart(item.key, item.title)]}
                                                layout={{ height: 250, title: { text: item.title, font: { size: 12 } }, margin: { l: 10, r: 10, t: 40, b: 10 }, showlegend: false }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Drill-down table */}
                {result && result.drillDown && result.drillDown.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Campaign Drill-Down</h5>
                            <div className="table-responsive">
                                <table className="table table-sm table-striped">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Campaign</th><th>Placement</th><th>Impressions</th><th>Clicks</th>
                                            <th>Orders</th><th>Spend</th><th>Sales</th><th>ACOS%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.drillDown.slice(0, 100).map(function (r, i) {
                                            return (
                                                <tr key={i}>
                                                    <td><small>{r.campaign}</small></td>
                                                    <td>{r.placement}</td>
                                                    <td>{r.impressions ? r.impressions.toLocaleString() : 0}</td>
                                                    <td>{r.clicks ? r.clicks.toLocaleString() : 0}</td>
                                                    <td>{r.orders ? r.orders.toLocaleString() : 0}</td>
                                                    <td>${r.spend ? r.spend.toFixed(2) : '0.00'}</td>
                                                    <td>${r.sales ? r.sales.toFixed(2) : '0.00'}</td>
                                                    <td>{r.acos ? r.acos.toFixed(2) + '%' : '0.00%'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
}

export default PlacementsSP;
